import { useState } from 'react';
import { Check, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import type { ToolCallStep } from '../scenarios';
import type { StepPhase } from '../useScenarioPlayer';

interface ToolCallLineProps {
  step: ToolCallStep;
  phase: StepPhase;
}

/** Slim, quiet one-liner for a tool call — expandable for args / result rows. */
export const ToolCallLine = ({ step, phase }: ToolCallLineProps): JSX.Element => {
  const [expanded, setExpanded] = useState(false);
  const isDone = phase === 'done';
  const expandable = Object.keys(step.args).length > 0 || !!step.rows;

  return (
    <div className="pl-10 text-xs">
      <button
        type="button"
        className="group flex items-center gap-1.5 text-neutral-400 transition-colors hover:text-neutral-600"
        onClick={() => expandable && setExpanded((v) => !v)}
      >
        {isDone ? (
          <Check className="h-3 w-3 shrink-0 text-green-500" />
        ) : (
          <Loader2 className="h-3 w-3 shrink-0 animate-spin text-blue-400" />
        )}
        <code className="font-mono">{step.name}</code>
        {isDone && <span className="text-neutral-400">· {step.summary}</span>}
        {expandable &&
          (expanded ? (
            <ChevronDown className="h-3 w-3 shrink-0 opacity-0 group-hover:opacity-100" />
          ) : (
            <ChevronRight className="h-3 w-3 shrink-0 opacity-0 group-hover:opacity-100" />
          ))}
      </button>

      {expanded && (
        <div className="mt-1.5 max-w-xl space-y-1.5">
          {Object.keys(step.args).length > 0 && (
            <div className="rounded-md bg-neutral-50 p-2 font-mono text-[11px] leading-relaxed text-neutral-500">
              {Object.entries(step.args).map(([key, value]) => (
                <div key={key}>
                  <span className="text-neutral-400">{key}:</span> {value}
                </div>
              ))}
            </div>
          )}
          {isDone && step.rows && step.rows.length > 0 && (
            <div className="overflow-x-auto rounded-md border border-neutral-200">
              <table className="w-full text-left text-[11px]">
                <thead>
                  <tr className="border-b border-neutral-200 bg-neutral-50">
                    {Object.keys(step.rows[0]).map((col) => (
                      <th key={col} className="px-2 py-1 font-medium text-neutral-500">
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {step.rows.map((row, i) => (
                    <tr key={i} className="border-b border-neutral-100 last:border-0">
                      {Object.values(row).map((cell, j) => (
                        <td key={j} className="px-2 py-1 text-neutral-600">
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
